import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchSurvey, submitSurvey } from '../api/survey';
import type { SurveyAnswers, SurveyOtherText, SurveyQuestion } from '../types/api';

interface OnboardingSurvey {
  /** The questions to render. Empty means render nothing. */
  questions: SurveyQuestion[];
  /** Dialog title and opening paragraph, both authored in the backend registry. */
  title: string;
  intro: string;
  submitting: boolean;
  submit: (answers: SurveyAnswers, otherText: SurveyOtherText) => Promise<void>;
}

/**
 * The onboarding survey: whether to ask it, and what happens when it is answered.
 *
 * Asked once per person, and the backend is what remembers that: a stored answer makes
 * every later `GET /survey` reply `show: false`. So nothing here is persisted, and
 * reloading after answering does not bring the dialog back.
 *
 * `eligible` is the caller's judgement that the question can be asked yet, which means
 * signed in and with a product resolved, since the wording names it. The request goes
 * out once per page load, guarded by a ref.
 *
 * Every failure path leaves the questions empty, which means no dialog and an app
 * somebody can use. That matters more here than it did when this was a card at the
 * bottom of a conversation: the dialog cannot be dismissed, so a survey that failed to
 * load and rendered anyway would be a locked door with nothing behind it.
 */
export function useOnboardingSurvey(eligible: boolean, product: string | null): OnboardingSurvey {
  const [questions, setQuestions] = useState<SurveyQuestion[]>([]);
  const [version, setVersion] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [intro, setIntro] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const asked = useRef(false);
  // The product the questions were worded against. Submitted instead of the current
  // product, so starting a new conversation under another product while the card is
  // still open cannot store a tenure answer against a product nobody was asked about.
  const askedProduct = useRef<string | null>(null);

  useEffect(() => {
    if (!eligible || asked.current) return;
    asked.current = true;
    askedProduct.current = product;
    // No stale-response guard here, deliberately, and it took a live run to see why one
    // is wrong. A ref survives StrictMode's remount while a cleanup flag does not, so
    // the two together cancelled the only request that was ever going to be made: the
    // first effect started the fetch, its cleanup marked the answer stale, and the
    // second effect returned early on the ref. Setting state after an unmount is a
    // harmless no-op in React 18, so the ref alone is the whole guard.
    fetchSurvey(product)
      .then((prompt) => {
        if (!prompt.show) return;
        setQuestions(prompt.questions);
        setVersion(prompt.version);
        setTitle(prompt.title ?? '');
        setIntro(prompt.intro ?? '');
      })
      .catch(() => {
        // Nothing to tell the user: no card is the same as no survey.
      });
    // Switching product re-runs this effect, and the ref is what makes that a no-op.
    // The question wording is fixed at the moment we ask, which is the product the
    // person was actually using: re-asking them under a second product would be a
    // second survey.
  }, [eligible, product]);

  const submit = useCallback(
    async (answers: SurveyAnswers, otherText: SurveyOtherText) => {
      if (!version) return;
      setSubmitting(true);
      try {
        await submitSurvey(version, askedProduct.current, answers, otherText);
      } catch {
        // The dialog closes even when the write failed, which is the safe direction
        // for a dialog nobody can dismiss: the alternative is somebody locked out of
        // the app by a failing request. Nothing was stored, so they are asked again on
        // their next visit, and the answers they just gave are lost. Losing an answer
        // is a smaller harm than a door that will not open.
      } finally {
        setSubmitting(false);
        setQuestions([]);
      }
    },
    [version],
  );

  return { questions, title, intro, submitting, submit };
}
